import { makeObservable } from 'mobx';

import { concatPath, toQueryString } from '@feathr/hooks';
import type {
  IBaseAttributes,
  IMetadata,
  IPagination,
  IRachisMessage,
  IWretchResponseValid,
  TConstraints,
} from '@feathr/rachis';
import { Collection, isWretchError, Model, wretch } from '@feathr/rachis';

import type { IEmailBaseCampaign } from './campaigns';

export interface IFeatureUsageRecord extends IBaseAttributes {
  account: string;
  type: 'EmailUsageRecord';
  record_date: string;
  finalized: boolean;
}

export interface IDailyUsageTotal {
  total: number;
  usage_date: string;
}

export interface ICampaignUsageTotal extends IBaseAttributes {
  campaign: IEmailBaseCampaign;
  total: number;
}

export interface IBillingUsage {
  unit_price: number;
  unit_quantity: number;
  unit_size: number;
  quota: number;
}

export interface IEmailUsageRecord extends IFeatureUsageRecord {
  total: number;
  billing: IBillingUsage;
  campaign_totals: ICampaignUsageTotal[];
  daily_totals: IDailyUsageTotal[];
}

export interface IEmailUsageRecordParams {
  page?: IPagination;
  target_date?: string;
}

interface IExportProps {
  email: string;
  targetDate?: string;
}

export class EmailUsageRecord extends Model<IEmailUsageRecord> {
  public readonly className = 'EmailUsageRecord';

  public get constraints(): TConstraints<IEmailUsageRecord> {
    return {};
  }

  constructor(attributes: Partial<IEmailUsageRecord> = {}) {
    super(attributes);
    makeObservable(this);
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/email_usage_records/${this.id}`, pathSuffix);
  }
}

export class EmailUsageRecords<Model extends EmailUsageRecord> extends Collection<Model> {
  public getClassName(): string {
    return 'email_usage_records';
  }

  public override url(): string {
    return `${this.getHostname()}email_usage_records/`;
  }

  public getModel(attributes: Partial<IEmailUsageRecord>): Model {
    return new EmailUsageRecord(attributes) as Model;
  }

  public async getUsageByMonth(params?: IEmailUsageRecordParams): Promise<IEmailUsageRecord> {
    const headers = this.getHeaders();
    const queryString = params ? `?${toQueryString(params)}` : '';
    const url = `${this.url()}date${queryString}`;

    const response = await wretch<IEmailUsageRecord>(url, {
      method: 'GET',
      headers,
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  // Campaign breakdown endpoint: GET /email_usage_records/campaigns
  public async getCampaignBreakdown(
    params?: IEmailUsageRecordParams,
  ): Promise<IWretchResponseValid<ICampaignUsageTotal[], IMetadata>> {
    const headers = this.getHeaders();
    const queryString = params ? `?${toQueryString(params)}` : '';
    const url = `${this.url()}campaigns${queryString}`;

    const response = await wretch<ICampaignUsageTotal[]>(url, {
      method: 'GET',
      headers,
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response;
  }

  public async exportMonthlyBreakdown({ email }: IExportProps): Promise<IRachisMessage> {
    const headers = this.getHeaders();
    const url = `${this.url()}export/monthly-email-sends-breakdown`;
    const response = await wretch<IRachisMessage>(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async exportCampaignBreakdown({
    email,
    targetDate,
  }: IExportProps): Promise<IRachisMessage> {
    const headers = this.getHeaders();
    const url = `${this.url()}export/campaign-email-sends-breakdown`;
    const response = await wretch<IRachisMessage>(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, target_date: targetDate }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async exportDailyEmailSends({ email, targetDate }: IExportProps): Promise<IRachisMessage> {
    const headers = this.getHeaders();
    const url = `${this.url()}export/daily-email-sends`;
    const response = await wretch<IRachisMessage>(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, target_date: targetDate }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}
